/** Touch / phone-play map gestures. Pure, so a test can drive them.
 *
 * Desktop: hover previews a walk, click commits it.
 * Phone: there is no hover, so the first tap on a walk/visit cell arms the
 * path and the same cell again commits. Selecting a hero or a town still
 * happens on the first tap - those are not walks.
 */

export interface TouchCell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Pixels of movement before a press is a pan, not a tap. */
export const TOUCH_PAN_SLOP_PX = 12;

/** Hold still this long to open the info card (desktop right-press). */
export const LONG_PRESS_MS = 450;

/** Pinch distance change, in CSS px, that steps the map zoom one level. */
export const PINCH_ZOOM_STEP_PX = 64;

export function sameCell(a: TouchCell | null, b: TouchCell): boolean {
  return a !== null && a.x === b.x && a.y === b.y && a.z === b.z;
}

/**
 * First tap on a walk/visit cell arms it; the same cell again confirms.
 * A different cell replaces the pending one.
 */
export function confirmOrArm(
  pending: TouchCell | null, cell: TouchCell,
): { readonly pending: TouchCell | null; readonly confirm: boolean } {
  if (sameCell(pending, cell)) return { pending: null, confirm: true };
  return { pending: cell, confirm: false };
}

export function pointerDistance(
  a: { readonly x: number; readonly y: number },
  b: { readonly x: number; readonly y: number },
): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * How many zoom steps a pinch of `delta` CSS pixels should take, relative
 * to the distance it started at. Positive is zoom in.
 */
export function pinchZoomSteps(startDist: number, nowDist: number): number {
  if (!(startDist > 0) || !(nowDist > 0)) return 0;
  return Math.trunc((nowDist - startDist) / PINCH_ZOOM_STEP_PX);
}
